import { createClient } from '@supabase/supabase-js'

// Supabase configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://demo.supabase.co'
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'demo-key'

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database types
export type UserRole = 'guest' | 'user' | 'admin' | 'rakerdiver';

export interface Profile {
  id: string
  email: string
  full_name?: string
  phone?: string
  role?: UserRole
  created_at: string
  updated_at: string
  // New fields for import and enhanced functionality
  legacy_row_id?: string
  pdga_number?: number
  facebook_profile?: string
  instagram_handle?: string
  sms_number?: string
  phone_number?: string
  avatar_url?: string
  default_source_id?: string
}

export type ReturnStatus = 'Found' | 'Returned to Owner' | 'Donated' | 'Sold' | 'Trashed' | 'For Sale Used';

export interface ContactAttempt {
  id: string;
  found_disc_id: string;
  attempted_at: string;
  contact_method: string;
  message_content: string;
  attempted_by_profile_id?: string;
  attempted_by_name?: string;
  response_received: boolean;
  response_content?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface Source {
  id: string
  name: string
  description?: string
  is_active: boolean
  sort_order: number
  legacy_row_id?: string
  msg1_found_just_entered?: string
  msg2_reminder?: string
  created_at: string
  updated_at: string
  created_by?: string
  updated_by?: string
}

export type DiscCondition = 'new' | 'excellent' | 'good' | 'fair' | 'poor' | 'not specified'
export type DiscType = 'putter' | 'approach' | 'midrange' | 'fairway_driver' | 'distance_driver'

export interface FoundDisc {
  id: string
  finder_id: string
  rack_id?: number
  brand: string
  mold?: string
  disc_type?: DiscType
  color: string
  weight?: number
  condition?: DiscCondition
  plastic_type?: string
  stamp_text?: string
  phone_number?: string
  name_on_disc?: string
  owner_email?: string
  owner_pdga_number?: number | string
  location_found: string
  location_coordinates?: { x: number; y: number }
  source_id?: string
  source_name?: string
  found_date: string
  description?: string
  image_urls?: string[]
  status: 'active' | 'claimed' | 'expired' | 'spam'
  return_status?: ReturnStatus
  returned_at?: string
  returned_notes?: string
  created_at: string
  updated_at: string
}

export interface LostDisc {
  id: string
  owner_id: string
  brand: string
  model?: string
  disc_type?: 'driver' | 'fairway_driver' | 'midrange' | 'putter' | 'approach' | 'distance_driver'
  color: string
  weight?: number
  plastic_type?: string
  stamp_text?: string
  location_lost: string
  location_coordinates?: { x: number; y: number }
  lost_date: string
  description?: string
  reward_offered?: number
  contact_preference: string
  status: 'active' | 'claimed' | 'expired' | 'spam'
  created_at: string
  updated_at: string
}

export interface BulkTurnin {
  id: string
  rakerdiver_id: string
  location_collected: string
  collection_date: string
  collection_time?: string
  disc_count: number
  turnin_location: string
  turnin_date: string
  turnin_time?: string
  notes?: string
  admin_verified: boolean
  verified_by?: string
  verified_at?: string
  verification_notes?: string
  created_at: string
  updated_at: string
  total_payments?: number
  confirmed_payments?: number
}

export interface BulkTurninPayment {
  id: string
  bulk_turnin_id: string
  amount: number
  payment_method?: string
  payment_date?: string
  payment_notes?: string
  created_by: string
  rakerdiver_confirmed: boolean
  confirmed_at?: string
  created_at: string
  updated_at: string
}

export interface AdminBulkTurnin extends BulkTurnin {
  rakerdiver_name?: string
  rakerdiver_email?: string
  verified_by_name?: string
  payment_count?: number
}

export interface DiscMatch {
  id: string
  found_disc_id: string
  lost_disc_id: string
  match_score: number
  status: 'potential' | 'confirmed' | 'rejected'
  finder_contacted_at?: string
  owner_contacted_at?: string
  created_at: string
  updated_at: string
  found_disc?: FoundDisc
  lost_disc?: LostDisc
}

// Helper functions for database operations
// 'For Sale Used' discs are hidden from lists/search unless explicitly requested (admin filter)
const FOR_SALE_USED: ReturnStatus = 'For Sale Used';
// NULL return_status counts as 'Found', so a plain neq (which drops NULLs) isn't enough
const NOT_FOR_SALE_USED_FILTER = `return_status.is.null,return_status.neq."${FOR_SALE_USED}"`;

// OR-search clauses for non-text columns, which can't use ilike (it errors, and used to knock
// every search into the slow fallback). disc_type is an enum: match the values containing the
// term. owner_pdga_number is an integer: match exactly when the term is a number.
const DISC_TYPES = ['driver', 'fairway_driver', 'midrange', 'putter', 'approach', 'distance_driver', 'not specified'];

function nonTextOrClauses(term: string): string[] {
  const clauses: string[] = [];
  const lower = term.toLowerCase();
  const discTypes = DISC_TYPES.filter(t => t.includes(lower));
  if (discTypes.length) clauses.push(`disc_type.in.(${discTypes.map(t => `"${t}"`).join(',')})`);
  if (/^\d{1,9}$/.test(term)) clauses.push(`owner_pdga_number.eq.${term}`);
  return clauses;
}

// Digits for rack ID / phone substring matching, only when the term is itself a number
// (otherwise 'md3' would match every disc with a 3 in its rack ID or phone)
function numericSearchDigits(term: string): string {
  return /^[\d\s#()+.-]+$/.test(term) ? term.replace(/[^0-9]/g, '') : '';
}

function orderForSort(sortBy: 'newest' | 'oldest' | 'rack_id_asc' | 'rack_id_desc'): { column: string; ascending: boolean } {
  switch (sortBy) {
    case 'oldest': return { column: 'created_at', ascending: true };
    case 'rack_id_asc': return { column: 'rack_id', ascending: true };
    case 'rack_id_desc': return { column: 'rack_id', ascending: false };
    case 'newest':
    default: return { column: 'created_at', ascending: false };
  }
}

function excludeForSaleUsed<T extends { return_status?: ReturnStatus | null }>(discs: T[], includeForSaleUsed: boolean): T[] {
  return includeForSaleUsed ? discs : discs.filter(d => d.return_status !== FOR_SALE_USED);
}

export const discService = {
  // Ensure demo user exists
  async ensureDemoUser() {
    const demoUserId = '00000000-0000-0000-0000-000000000000';
    try {
      // Check if demo user exists
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', demoUserId)
        .single();

      if (!existingUser) {
        // Create demo user
        const { error } = await supabase
          .from('profiles')
          .insert([{
            id: demoUserId,
            email: 'demo@discfinder.app',
            full_name: 'Demo User'
          }]);

        if (error) {
          console.warn('Could not create demo user:', error);
        }
      }
      return demoUserId;
    } catch (error) {
      console.warn('Demo user setup failed:', error);
      return demoUserId;
    }
  },

  // Create a new found disc report
  async createFoundDisc(discData: Omit<FoundDisc, 'id' | 'created_at' | 'updated_at' | 'status'>) {
    try {
      const { data, error } = await supabase
        .from('found_discs')
        .insert([{
          ...discData,
          status: 'active'
        }])
        .select()
        .single()

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Error creating found disc:', error)
      return { data: null, error }
    }
  },

  // Get a single found disc by ID
  async getFoundDiscById(discId: string) {
    try {
      // Try the public view first
      let { data, error } = await supabase
        .from('public_found_discs')
        .select('*')
        .eq('id', discId)
        .single();

      // If public view doesn't work, try main table
      if (error || !data) {
        const result = await supabase
          .from('found_discs')
          .select('*')
          .eq('id', discId)
          .eq('status', 'active')
          .single();

        data = result.data;
        error = result.error;
      }

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching found disc by ID:', error);
      return { data: null, error };
    }
  },

  // Discs by ID from the public view (contact fields masked for non-admins), in chunks
  async fetchPublicDiscsByIds(ids: string[]): Promise<FoundDisc[]> {
    const discs: FoundDisc[] = [];
    for (let i = 0; i < ids.length; i += 500) {
      const { data } = await supabase
        .from('public_found_discs')
        .select('*')
        .in('id', ids.slice(i, i + 500));
      if (data) discs.push(...data);
    }
    return discs;
  },

  // Exact owner email or PDGA number. Those fields are hidden from non-admins in the view, so the
  // match happens in the database and only IDs come back
  async findDiscsByOwner(term: string): Promise<FoundDisc[]> {
    const trimmed = term.trim();
    if (!trimmed.includes('@') && !/^\d{1,9}$/.test(trimmed)) return [];
    const { data, error } = await supabase.rpc('search_disc_ids_by_owner', { p_term: trimmed });
    if (error || !data || data.length === 0) return [];
    return this.fetchPublicDiscsByIds((data as { id: string }[]).map(r => r.id));
  },

  // Get all FAQs
  async getFAQs() {
    try {
      const { data, error } = await supabase
        .from('t_faq')
        .select('*')
        .order('id', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      return { data: null, error };
    }
  },

  // Admin-only: get discs in a rack_id range regardless of return_status
  async getFoundDiscsInRangeAllStatuses(min: number, max: number, options: {
    limit?: number;
    offset?: number;
    fetchAll?: boolean;
    includeForSaleUsed?: boolean;
  } = {}) {
    try {
      const { limit = 500, offset = 0, fetchAll = false, includeForSaleUsed = false } = options;

      const rangeQuery = (count?: 'exact') => {
        let query = supabase
          .from('found_discs')
          .select('*', count ? { count } : undefined)
          .eq('status', 'active')
          .gte('rack_id', min)
          .lte('rack_id', max);
        if (!includeForSaleUsed) query = query.or(NOT_FOR_SALE_USED_FILTER);
        return query.order('rack_id', { ascending: true });
      };

      if (fetchAll) {
        // Fetch all in range using chunking
        const allDiscs: FoundDisc[] = [];
        let chunkOffset = 0;
        const chunkSize = 1000;
        let hasMore = true;

        while (hasMore) {
          const { data, error } = await rangeQuery()
            .range(chunkOffset, chunkOffset + chunkSize - 1);

          if (error) throw error;
          if (data && data.length > 0) {
            allDiscs.push(...data);
            hasMore = data.length === chunkSize;
            chunkOffset += chunkSize;
          } else {
            hasMore = false;
          }
        }

        return { data: allDiscs, error: null, count: allDiscs.length, hasMore: false, nextOffset: 0 };
      } else {
        const { data, error, count } = await rangeQuery('exact')
          .range(offset, offset + limit - 1);

        if (error) throw error;
        return { data, error: null, count, hasMore: !!(data && data.length === limit), nextOffset: offset + limit };
      }
    } catch (error) {
      console.error('Error fetching found discs in range (all statuses):', error);
      return { data: null, error, count: 0, hasMore: false, nextOffset: 0 };
    }
  },

  // Get all active found discs with chunking to handle large datasets
  async getFoundDiscs(options: {
    limit?: number;
    offset?: number;
    fetchAll?: boolean;
    sortBy?: 'newest' | 'oldest' | 'rack_id_asc' | 'rack_id_desc';
    minRackId?: number;
    maxRackId?: number;
    includeForSaleUsed?: boolean;
  } = {}) {
    try {
      const { limit = 1000, offset = 0, fetchAll = true, sortBy = 'newest', minRackId, maxRackId, includeForSaleUsed = false } = options;

      if (fetchAll) {
        // Fetch all records using chunking
        const result = await this.getAllFoundDiscsChunked(includeForSaleUsed);
        if (result.data) {
          // Apply client-side filtering and sorting
          let filteredData = result.data;

          // Apply rack ID filters
          if (minRackId !== undefined) {
            filteredData = filteredData.filter(disc => disc.rack_id && disc.rack_id >= minRackId);
          }
          if (maxRackId !== undefined) {
            filteredData = filteredData.filter(disc => disc.rack_id && disc.rack_id <= maxRackId);
          }

          // Apply sorting
          filteredData = this.sortFoundDiscs(filteredData, sortBy);

          return {
            ...result,
            data: filteredData,
            count: filteredData.length
          };
        }
        return result;
      } else {
        // Fetch specific page
        return await this.getFoundDiscsPage(limit, offset, sortBy, minRackId, maxRackId, includeForSaleUsed);
      }
    } catch (error) {
      console.error('Error fetching found discs:', error)
      return { data: null, error }
    }
  },

  // Helper method to sort found discs
  sortFoundDiscs(discs: FoundDisc[], sortBy: 'newest' | 'oldest' | 'rack_id_asc' | 'rack_id_desc'): FoundDisc[] {
    return [...discs].sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'rack_id_asc':
          return (a.rack_id || 0) - (b.rack_id || 0);
        case 'rack_id_desc':
          return (b.rack_id || 0) - (a.rack_id || 0);
        case 'newest':
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });
  },

  // Get a specific page of found discs
  async getFoundDiscsPage(limit: number = 50, offset: number = 0, sortBy: 'newest' | 'oldest' | 'rack_id_asc' | 'rack_id_desc' = 'newest', minRackId?: number, maxRackId?: number, includeForSaleUsed: boolean = false) {
    try {
      // Determine sort order
      let orderColumn = 'created_at';
      let ascending = false;

      switch (sortBy) {
        case 'oldest':
          orderColumn = 'created_at';
          ascending = true;
          break;
        case 'rack_id_asc':
          orderColumn = 'rack_id';
          ascending = true;
          break;
        case 'rack_id_desc':
          orderColumn = 'rack_id';
          ascending = false;
          break;
        case 'newest':
        default:
          orderColumn = 'created_at';
          ascending = false;
          break;
      }

      // Try the public view first (no status filter needed - view already filters for active discs)
      let query = supabase
        .from('public_found_discs')
        .select('*', { count: 'exact' });

      // Apply rack ID filters
      if (minRackId !== undefined) {
        query = query.gte('rack_id', minRackId);
      }
      if (maxRackId !== undefined) {
        query = query.lte('rack_id', maxRackId);
      }
      if (!includeForSaleUsed) {
        query = query.or(NOT_FOR_SALE_USED_FILTER);
      }

      let { data, error, count } = await query
        .order(orderColumn, { ascending })
        .range(offset, offset + limit - 1);

      // If public view query fails, use main table with status filter
      if (error) {
        console.log('Public view failed, using main table instead:', error.message);
        let mainQuery = supabase
          .from('found_discs')
          .select('*', { count: 'exact' })
          .eq('status', 'active');

        // Apply rack ID filters
        if (minRackId !== undefined) {
          mainQuery = mainQuery.gte('rack_id', minRackId);
        }
        if (maxRackId !== undefined) {
          mainQuery = mainQuery.lte('rack_id', maxRackId);
        }
        if (!includeForSaleUsed) {
          mainQuery = mainQuery.or(NOT_FOR_SALE_USED_FILTER);
        }

        const result = await mainQuery
          .order(orderColumn, { ascending })
          .range(offset, offset + limit - 1);

        data = result.data;
        error = result.error;
        count = result.count;
      }

      if (error) throw error
      return {
        data,
        error: null,
        count,
        hasMore: data && data.length === limit,
        nextOffset: offset + limit
      }
    } catch (error) {
      console.error('Error fetching found discs page:', error)
      return { data: null, error, count: 0, hasMore: false, nextOffset: 0 }
    }
  },

  // Get all found discs using chunked fetching to bypass 1000 record limit
  async getAllFoundDiscsChunked(includeForSaleUsed: boolean = false) {
    try {
      const allDiscs: FoundDisc[] = [];
      let offset = 0;
      const chunkSize = 1000;
      let hasMore = true;

      console.log('Fetching all found discs using chunked approach...');

      while (hasMore) {
        console.log(`Fetching chunk at offset ${offset}...`);

        // Try the public view first, fallback to main table
        let viewQuery = supabase
          .from('public_found_discs')
          .select('*');
        if (!includeForSaleUsed) viewQuery = viewQuery.or(NOT_FOR_SALE_USED_FILTER);
        let { data, error } = await viewQuery
          .order('created_at', { ascending: false })
          .range(offset, offset + chunkSize - 1)

        // If public view doesn't exist or doesn't have image_urls, use main table
        if (error || (data && data.length > 0 && !data[0].hasOwnProperty('image_urls'))) {
          if (offset === 0) console.log('Using main table instead of view');
          let mainQuery = supabase
            .from('found_discs')
            .select('*')
            .eq('status', 'active');
          if (!includeForSaleUsed) mainQuery = mainQuery.or(NOT_FOR_SALE_USED_FILTER);
          const result = await mainQuery
            .order('created_at', { ascending: false })
            .range(offset, offset + chunkSize - 1);

          data = result.data;
          error = result.error;
        }

        if (error) throw error;

        if (data && data.length > 0) {
          allDiscs.push(...data);
          console.log(`Fetched ${data.length} discs, total so far: ${allDiscs.length}`);

          // If we got fewer records than requested, we've reached the end
          hasMore = data.length === chunkSize;
          offset += chunkSize;
        } else {
          hasMore = false;
        }
      }

      console.log(`Completed chunked fetch: ${allDiscs.length} total discs`);
      return {
        data: allDiscs,
        error: null,
        count: allDiscs.length,
        hasMore: false,
        nextOffset: 0
      }
    } catch (error) {
      console.error('Error in chunked fetch:', error)
      return { data: null, error, count: 0, hasMore: false, nextOffset: 0 }
    }
  },

  // Search found discs by criteria with chunking support
  async searchFoundDiscs(searchCriteria: {
    brand?: string
    mold?: string
    color?: string
    discType?: string
    locationFound?: string
    rackId?: string
  }, options: {
    limit?: number;
    offset?: number;
    fetchAll?: boolean
  } = {}) {
    try {
      const { limit = 1000, offset = 0, fetchAll = true } = options;

      if (fetchAll) {
        // Fetch all matching records using chunking
        return await this.searchFoundDiscsChunked(searchCriteria);
      } else {
        // Fetch specific page
        return await this.searchFoundDiscsPage(searchCriteria, limit, offset);
      }
    } catch (error) {
      console.error('Error searching found discs:', error)
      return { data: null, error }
    }
  },

  // Search found discs with pagination
  async searchFoundDiscsPage(searchCriteria: {
    brand?: string
    mold?: string
    color?: string
    discType?: string
    locationFound?: string
    rackId?: string
  }, limit: number = 50, offset: number = 0) {
    try {
      // Try the public view first (no status filter needed - view already filters for active discs)
      let query = supabase
        .from('public_found_discs')
        .select('*', { count: 'exact' })

      if (searchCriteria.brand) {
        query = query.ilike('brand', `%${searchCriteria.brand}%`)
      }
      if (searchCriteria.mold) {
        query = query.ilike('mold', `%${searchCriteria.mold}%`)
      }
      if (searchCriteria.color) {
        query = query.ilike('color', `%${searchCriteria.color}%`)
      }
      if (searchCriteria.discType) {
        query = query.eq('disc_type', searchCriteria.discType)
      }
      if (searchCriteria.locationFound) {
        query = query.ilike('location_found', `%${searchCriteria.locationFound}%`)
      }
      if (searchCriteria.rackId) {
        const rackIdNum = parseInt(searchCriteria.rackId);
        if (!isNaN(rackIdNum)) {
          query = query.eq('rack_id', rackIdNum)
        }
      }

      let { data, error, count } = await query
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1)

      // If public view query fails, use main table with status filter
      if (error) {
        console.log('Public view search failed, using main table instead:', error.message);
        let mainQuery = supabase
          .from('found_discs')
          .select('*', { count: 'exact' })
          .eq('status', 'active')

        if (searchCriteria.brand) {
          mainQuery = mainQuery.ilike('brand', `%${searchCriteria.brand}%`)
        }
        if (searchCriteria.mold) {
          mainQuery = mainQuery.ilike('mold', `%${searchCriteria.mold}%`)
        }
        if (searchCriteria.color) {
          mainQuery = mainQuery.ilike('color', `%${searchCriteria.color}%`)
        }
        if (searchCriteria.discType) {
          mainQuery = mainQuery.eq('disc_type', searchCriteria.discType)
        }
        if (searchCriteria.locationFound) {
          mainQuery = mainQuery.ilike('location_found', `%${searchCriteria.locationFound}%`)
        }
        if (searchCriteria.rackId) {
          const rackIdNum = parseInt(searchCriteria.rackId);
          if (!isNaN(rackIdNum)) {
            mainQuery = mainQuery.eq('rack_id', rackIdNum)
          }
        }

        const result = await mainQuery
          .order('created_at', { ascending: false })
          .range(offset, offset + limit - 1);
        data = result.data;
        error = result.error;
        count = result.count;
      }

      if (error) throw error
      return {
        data,
        error: null,
        count,
        hasMore: data && data.length === limit,
        nextOffset: offset + limit
      }
    } catch (error) {
      console.error('Error searching found discs page:', error)
      return { data: null, error, count: 0, hasMore: false, nextOffset: 0 }
    }
  },

  // Search found discs using chunked fetching to get all results
  async searchFoundDiscsChunked(searchCriteria: {
    brand?: string
    mold?: string
    color?: string
    discType?: string
    locationFound?: string
    rackId?: string
  }) {
    try {
      const allDiscs: FoundDisc[] = [];
      let offset = 0;
      const chunkSize = 1000;
      let hasMore = true;

      console.log('Searching found discs using chunked approach...');

      while (hasMore) {
        console.log(`Searching chunk at offset ${offset}...`);

        const result = await this.searchFoundDiscsPage(searchCriteria, chunkSize, offset);

        if (result.error) throw result.error;

        if (result.data && result.data.length > 0) {
          allDiscs.push(...result.data);
          console.log(`Found ${result.data.length} discs, total so far: ${allDiscs.length}`);

          // If we got fewer records than requested, we've reached the end
          hasMore = result.data.length === chunkSize;
          offset += chunkSize;
        } else {
          hasMore = false;
        }
      }

      console.log(`Completed chunked search: ${allDiscs.length} total matching discs`);
      return {
        data: allDiscs,
        error: null,
        count: allDiscs.length,
        hasMore: false,
        nextOffset: 0
      }
    } catch (error) {
      console.error('Error in chunked search:', error)
      return { data: null, error, count: 0, hasMore: false, nextOffset: 0 }
    }
  },

  // Search found discs with a single query string across all fields
  async searchFoundDiscsWithQuery(searchQuery: string, options: {
    limit?: number;
    offset?: number;
    fetchAll?: boolean;
    sortBy?: 'newest' | 'oldest' | 'rack_id_asc' | 'rack_id_desc';
    minRackId?: number;
    maxRackId?: number;
    includeForSaleUsed?: boolean;
  } = {}) {
    try {
      if (!searchQuery || searchQuery.trim() === '') {
        // If no search query, return all discs
        return this.getFoundDiscs(options);
      }

      const { limit = 1000, offset = 0, fetchAll = true, sortBy = 'newest', minRackId, maxRackId, includeForSaleUsed = false } = options;

      // For simple single-term searches, use server-side filtering for better performance.
      // A phone number typed with spaces, e.g. "(316) 555-1234", is one digits-only term.
      const phoneDigits = numericSearchDigits(searchQuery.trim());
      const searchTerms = phoneDigits.length >= 7
        ? [phoneDigits]
        : searchQuery.trim().toLowerCase().split(/\s+/);

      if (searchTerms.length === 1) {
        // Single term - use server-side search for better performance
        return await this.searchFoundDiscsSingleTerm(searchTerms[0], options);
      } else {
        // Multi-term search - still need client-side filtering for complex logic
        if (fetchAll) {
          const result = await this.searchFoundDiscsMultiTermChunked(searchTerms, includeForSaleUsed);
          if (result.data) {
            // Apply client-side filtering and sorting
            let filteredData = result.data;

            // Apply rack ID filters
            if (minRackId !== undefined) {
              filteredData = filteredData.filter(disc => disc.rack_id && disc.rack_id >= minRackId);
            }
            if (maxRackId !== undefined) {
              filteredData = filteredData.filter(disc => disc.rack_id && disc.rack_id <= maxRackId);
            }

            // Apply sorting
            filteredData = this.sortFoundDiscs(filteredData, sortBy);

            return {
              ...result,
              data: filteredData,
              count: filteredData.length
            };
          }
          return result;
        } else {
          return await this.searchFoundDiscsMultiTermPaginated(searchTerms, limit, offset, sortBy, minRackId, maxRackId, includeForSaleUsed);
        }
      }
    } catch (error) {
      console.error('Error searching found discs with query:', error);
      return { data: null, error };
    }
  },

  // Optimized single-term search using server-side filtering
  async searchFoundDiscsSingleTerm(term: string, options: {
    limit?: number;
    offset?: number;
    fetchAll?: boolean;
    sortBy?: 'newest' | 'oldest' | 'rack_id_asc' | 'rack_id_desc';
    minRackId?: number;
    maxRackId?: number;
    includeForSaleUsed?: boolean;
  } = {}) {
    try {
      const { limit = 1000, offset = 0, fetchAll = true, sortBy = 'newest', minRackId, maxRackId, includeForSaleUsed = false } = options;

      if (fetchAll) {
        // Fetch all matching records using chunking
        const result = await this.searchFoundDiscsSingleTermChunked(term, includeForSaleUsed);
        if (result.data) {
          // Apply client-side filtering and sorting
          let filteredData = result.data;

          // Apply rack ID filters
          if (minRackId !== undefined) {
            filteredData = filteredData.filter(disc => disc.rack_id && disc.rack_id >= minRackId);
          }
          if (maxRackId !== undefined) {
            filteredData = filteredData.filter(disc => disc.rack_id && disc.rack_id <= maxRackId);
          }

          // Apply sorting
          filteredData = this.sortFoundDiscs(filteredData, sortBy);

          return {
            ...result,
            data: filteredData,
            count: filteredData.length
          };
        }
        return result;
      } else {
        // Fetch specific page
        return await this.searchFoundDiscsSingleTermPage(term, limit, offset, sortBy, minRackId, maxRackId, includeForSaleUsed);
      }
    } catch (error) {
      console.error('Error in single term search:', error);
      return { data: null, error };
    }
  },

  // Single-term search with pagination - optimized for rack_id searches
  async searchFoundDiscsSingleTermPage(term: string, limit: number, offset: number, sortBy: 'newest' | 'oldest' | 'rack_id_asc' | 'rack_id_desc' = 'newest', minRackId?: number, maxRackId?: number, includeForSaleUsed: boolean = false) {
    try {
      const lowerTerm = term.toLowerCase();
      // Handle rack ID searches - strip # prefix if present
      const cleanTerm = term.startsWith('#') ? term.substring(1) : term;
      const rackIdNum = parseInt(cleanTerm);
      const isNumericSearch = !isNaN(rackIdNum);

      // Capture rack_id and phone_number results to optionally merge with other matches
      let rackIdResults: FoundDisc[] | null | undefined = undefined;
      let phoneResults: FoundDisc[] | null | undefined = undefined;

      // If it's a numeric search (likely rack_id), try direct rack_id query and phone_number substring match
      if (isNumericSearch) {
        console.log(`Searching for rack_id: ${rackIdNum}`);

        // Try direct rack_id search first
        let { data: rackIdData, error: rackIdError } = await supabase
          .from('public_found_discs')
          .select('*')
          .eq('rack_id', rackIdNum);

        // If public view doesn't work, try main table
        if (rackIdError || !rackIdData) {
          console.log('Trying main table for rack_id search');
          const result = await supabase
            .from('found_discs')
            .select('*')
            .eq('status', 'active')
            .eq('rack_id', rackIdNum);

          rackIdData = result.data;
          rackIdError = result.error;
        }

        rackIdResults = rackIdData || [];

        // Also search for phone matches via RPC that returns only IDs (guest-safe)
        // Page through the phone RPC to bypass 1k row cap
        const phoneIdSet = new Set<string>();
        const digits = term.replace(/[^0-9]/g, '');
        const pageSize = 1000;
        for (let offsetIds = 0; offsetIds < 10000; offsetIds += pageSize) { // hard cap 10k ids
          const { data: phoneIdRows, error: phoneRpcError } = await supabase
            .rpc('search_disc_ids_by_phone_digits', { p_digits: digits, p_offset: offsetIds, p_limit: pageSize });
          if (phoneRpcError) {
            console.warn('phone RPC error:', phoneRpcError);
            break;
          }
          if (!phoneIdRows || phoneIdRows.length === 0) break;
          for (const r of phoneIdRows as any[]) phoneIdSet.add(r.id);
          if (phoneIdRows.length < pageSize) break;
        }
        if (phoneIdSet.size > 0) {
          // Fetch details for those IDs from the public view if possible, else main table, in chunks to avoid limits
          const phoneIds = Array.from(phoneIdSet);
          const phoneDataAgg: any[] = [];
          const chunkSize = 500;
          for (let i = 0; i < phoneIds.length; i += chunkSize) {
            const chunk = phoneIds.slice(i, i + chunkSize);
            let { data: phoneDataChunk, error: phoneFetchError } = await supabase
              .from('public_found_discs')
              .select('*')
              .in('id', chunk);
            if (phoneFetchError || !phoneDataChunk) {
              const result = await supabase
                .from('found_discs')
                .select('*')
                .eq('status', 'active')
                .in('id', chunk);
              phoneDataChunk = result.data || [];
            }
            if (phoneDataChunk && phoneDataChunk.length > 0) {
              phoneDataAgg.push(...phoneDataChunk);
            }
          }
          phoneResults = phoneDataAgg;
        } else {
          phoneResults = [];
        }

        if (rackIdError) {
          console.error('Rack ID search error:', rackIdError);
        }

        // Combine rack_id and phone results now; other OR search will merge further below
        if (Array.isArray(rackIdResults) || Array.isArray(phoneResults)) {
          const seen = new Set<string>();
          const combined = [
            ...(Array.isArray(rackIdResults) ? rackIdResults : []),
            ...(Array.isArray(phoneResults) ? phoneResults : [])
          ].filter(d => (seen.has(d.id) ? false : (seen.add(d.id), true)));
          rackIdResults = excludeForSaleUsed(combined, includeForSaleUsed); // reuse for later merging with OR
        }
      }

      // Exact owner email / PDGA matches (masked in the view for non-admins, so matched server-side)
      const ownerMatches = excludeForSaleUsed(await this.findDiscsByOwner(term), includeForSaleUsed);
      if (ownerMatches.length > 0) {
        const known = new Set((rackIdResults || []).map(d => d.id));
        rackIdResults = [...(rackIdResults || []), ...ownerMatches.filter(d => !known.has(d.id))];
      }

      // If not a rack_id hit, try a direct UUID (id) match
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (uuidRegex.test(term)) {
        let { data: idResults, error: idError } = await supabase
          .from('public_found_discs')
          .select('*')
          .eq('id', term);

        if (idError || !idResults) {
          const result = await supabase
            .from('found_discs')
            .select('*')
            .eq('status', 'active')
            .eq('id', term);
          idResults = result.data;
          idError = result.error;
        }

        if (idResults) idResults = excludeForSaleUsed(idResults, includeForSaleUsed);
        if (!idError && idResults && idResults.length > 0) {
          return {
            data: idResults,
            error: null,
            count: idResults.length,
            hasMore: false,
            nextOffset: 0
          };
        }
      }

      // Try a server-side multi-field search (public view first)
      try {
        const likeTerm = `%${term}%`;
        const textOrFilter = [
          `brand.ilike.${likeTerm}`,
          `mold.ilike.${likeTerm}`,
          `color.ilike.${likeTerm}`,
          `location_found.ilike.${likeTerm}`,
          `description.ilike.${likeTerm}`,
          `stamp_text.ilike.${likeTerm}`,
          `phone_number.ilike.${likeTerm}`,
          `name_on_disc.ilike.${likeTerm}`,
          `plastic_type.ilike.${likeTerm}`,
          ...nonTextOrClauses(term),
          `owner_email.ilike.${likeTerm}`
        ].join(',');
        const { column: orderColumn, ascending } = orderForSort(sortBy);

        // Fetch every OR match up to the end of the requested page (in final sort order), so the
        // rack/phone matches can be merged in and the page sliced client-side
        const orQuery = (table: 'public_found_discs' | 'found_discs') => {
          let query = supabase.from(table).select('*', { count: 'exact' }).or(textOrFilter);
          if (table === 'found_discs') query = query.eq('status', 'active');
          if (!includeForSaleUsed) query = query.or(NOT_FOR_SALE_USED_FILTER);
          return query.order(orderColumn, { ascending }).range(0, offset + limit - 1);
        };

        let { data: orData, error: orError, count: orCount } = await orQuery('public_found_discs');

        // If public view OR search fails, try main table with status filter
        if (orError) {
          const res = await orQuery('found_discs');
          orData = res.data as any[] | null;
          orError = res.error;
          orCount = res.count;
        }

        if (!orError && orData) {
          // Merge rack_id/phone results (if any) with OR results and de-duplicate
          const orIds = new Set(orData.map((d: any) => d.id));
          const extras = (Array.isArray(rackIdResults) ? rackIdResults : []).filter(d => !orIds.has(d.id));
          const combined = [...extras, ...orData];

          const sorted = this.sortFoundDiscs(combined as any, sortBy);
          const paged = sorted.slice(offset, offset + limit);
          const total = (orCount ?? orData.length) + extras.length;

          return {
            data: paged,
            error: null,
            count: total,
            hasMore: offset + limit < total,
            nextOffset: offset + limit
          };
        }
      } catch (e) {
        // Non-fatal; fall through to full search
        console.warn('OR search failed, falling back to full search');
      }

      // If no exact rack_id match or it's a text search, fall back to full search
      console.log(`Performing full search for term: "${term}"`);

      // Get all discs and filter client-side for comprehensive search
      const { data: allDiscs, error: fetchError } = await this.getAllFoundDiscsChunked(includeForSaleUsed);

      if (fetchError) {
        throw fetchError;
      }

      if (!allDiscs || allDiscs.length === 0) {
        return { data: [], error: null, count: 0, hasMore: false, nextOffset: 0 };
      }

      // Filter discs that match the search term
      let filteredDiscs = allDiscs.filter(disc => {
        const textFields = [
          disc.brand,
          disc.mold,
          disc.color,
          disc.location_found,
          disc.description,
          disc.stamp_text,
          disc.phone_number,
          disc.name_on_disc,
          disc.owner_email,
          disc.owner_pdga_number ? String(disc.owner_pdga_number) : undefined,
          disc.plastic_type,
          disc.disc_type
        ];

        const matchesText = textFields.some(field =>
          field && field.toLowerCase().includes(lowerTerm)
        );

        const matchesRackId = isNumericSearch && disc.rack_id === rackIdNum;

        // Numeric substring match on rack_id
        const termDigits = numericSearchDigits(term);
        const rackDigits = (disc.rack_id !== undefined && disc.rack_id !== null) ? String(disc.rack_id) : '';
        const matchesRackDigits = termDigits ? rackDigits.includes(termDigits) : false;

        // Numeric phone match ignoring formatting (e.g., (316) 992-7729 should match 7729)
        const phoneDigits = (disc.phone_number || '').replace(/[^0-9]/g, '');
        const matchesPhoneDigits = termDigits ? phoneDigits.includes(termDigits) : false;

        return matchesText || matchesRackId || matchesRackDigits || matchesPhoneDigits;
      });

      // Ensure rack_id/phone matches are included (merge/dedupe)
      const rackList: FoundDisc[] = Array.isArray(rackIdResults) ? (rackIdResults as FoundDisc[]) : [];
      if (rackList.length > 0) {
        const byId = new Map(filteredDiscs.map(d => [d.id, d]));
        for (const d of rackList) {
          byId.set(d.id, d);
        }
        filteredDiscs = Array.from(byId.values());
      }

      console.log(`Full search found ${filteredDiscs.length} matching discs`);

      // Apply pagination to filtered results
      const paginatedResults = filteredDiscs.slice(offset, offset + limit);

      return {
        data: paginatedResults,
        error: null,
        count: filteredDiscs.length,
        hasMore: offset + limit < filteredDiscs.length,
        nextOffset: offset + limit
      };
    } catch (error) {
      console.error('Error in single term search page:', error);
      return { data: null, error, count: 0, hasMore: false, nextOffset: 0 };
    }
  },

  // Single-term search using chunked fetching - optimized for rack_id searches
  async searchFoundDiscsSingleTermChunked(term: string, includeForSaleUsed: boolean = false) {
    try {
      const lowerTerm = term.toLowerCase();
      // Handle rack ID searches - strip # prefix if present
      const cleanTerm = term.startsWith('#') ? term.substring(1) : term;
      const rackIdNum = parseInt(cleanTerm);
      const isNumericSearch = !isNaN(rackIdNum);

      console.log(`Searching for single term "${term}" using chunked approach...`);

      // Capture rack_id and phone_number results to optionally merge with other matches
      let rackIdResults: FoundDisc[] | null | undefined = undefined;
      let phoneResults: FoundDisc[] | null | undefined = undefined;

      // If it's a numeric search (likely rack_id), try direct rack_id query first
      if (isNumericSearch) {
        console.log(`Trying direct rack_id search for: ${rackIdNum}`);

        // Try direct rack_id search first
        let { data: rackIdData, error: rackIdError } = await supabase
          .from('public_found_discs')
          .select('*')
          .eq('rack_id', rackIdNum);

        // If public view doesn't work, try main table
        if (rackIdError || !rackIdData) {
          console.log('Trying main table for rack_id search');
          const result = await supabase
            .from('found_discs')
            .select('*')
            .eq('status', 'active')
            .eq('rack_id', rackIdNum);

          rackIdData = result.data;
          rackIdError = result.error;
        }

        if (rackIdError) {
          console.error('Rack ID search error:', rackIdError);
        }
        rackIdResults = rackIdData || [];

        // Also search for phone matches via RPC that returns only IDs (guest-safe)
        // Page through the phone RPC to bypass 1k row cap
        const phoneIdSet = new Set<string>();
        const digits = term.replace(/[^0-9]/g, '');
        const pageSize = 1000;
        for (let offsetIds = 0; offsetIds < 10000; offsetIds += pageSize) { // hard cap 10k ids
          const { data: phoneIdRows, error: phoneRpcError } = await supabase
            .rpc('search_disc_ids_by_phone_digits', { p_digits: digits, p_offset: offsetIds, p_limit: pageSize });
          if (phoneRpcError) {
            console.warn('phone RPC error:', phoneRpcError);
            break;
          }
          if (!phoneIdRows || phoneIdRows.length === 0) break;
          for (const r of phoneIdRows as any[]) phoneIdSet.add(r.id);
          if (phoneIdRows.length < pageSize) break;
        }
        if (phoneIdSet.size > 0) {
          // Fetch details for those IDs from the public view if possible, else main table, in chunks to avoid limits
          const phoneIds = Array.from(phoneIdSet);
          const phoneDataAgg: any[] = [];
          const chunkSize = 500;
          for (let i = 0; i < phoneIds.length; i += chunkSize) {
            const chunk = phoneIds.slice(i, i + chunkSize);
            let { data: phoneDataChunk, error: phoneFetchError } = await supabase
              .from('public_found_discs')
              .select('*')
              .in('id', chunk);
            if (phoneFetchError || !phoneDataChunk) {
              const result = await supabase
                .from('found_discs')
                .select('*')
                .eq('status', 'active')
                .in('id', chunk);
              phoneDataChunk = result.data || [];
            }
            if (phoneDataChunk && phoneDataChunk.length > 0) {
              phoneDataAgg.push(...phoneDataChunk);
            }
          }
          phoneResults = phoneDataAgg;
        } else {
          phoneResults = [];
        }

        // Combine rack_id and phone results
        if (Array.isArray(rackIdResults) || Array.isArray(phoneResults)) {
          const seen = new Set<string>();
          const combined = [
            ...(Array.isArray(rackIdResults) ? rackIdResults : []),
            ...(Array.isArray(phoneResults) ? phoneResults : [])
          ].filter(d => (seen.has(d.id) ? false : (seen.add(d.id), true)));
          rackIdResults = excludeForSaleUsed(combined, includeForSaleUsed); // reuse for later merging with filtered list
        }
      }

      // Exact owner email / PDGA matches (masked in the view for non-admins, so matched server-side)
      const ownerMatches = excludeForSaleUsed(await this.findDiscsByOwner(term), includeForSaleUsed);
      if (ownerMatches.length > 0) {
        const known = new Set((rackIdResults || []).map(d => d.id));
        rackIdResults = [...(rackIdResults || []), ...ownerMatches.filter(d => !known.has(d.id))];
      }

      // If not a rack_id hit, try a direct UUID (id) match for chunked search
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (uuidRegex.test(term)) {
        let { data: idResults, error: idError } = await supabase
          .from('public_found_discs')
          .select('*')
          .eq('id', term);

        if (idError || !idResults) {
          const result = await supabase
            .from('found_discs')
            .select('*')
            .eq('status', 'active')
            .eq('id', term);
          idResults = result.data;
          idError = result.error;
        }

        if (idResults) idResults = excludeForSaleUsed(idResults, includeForSaleUsed);
        if (!idError && idResults && idResults.length > 0) {
          return {
            data: idResults,
            error: null,
            count: idResults.length,
            hasMore: false,
            nextOffset: 0
          };
        }
      }

      // Try a server-side multi-field search (public view first) for chunked path, then merge with rack_id
      try {
        const likeTerm = `%${term}%`;
        let { data: orData, error: orError } = await supabase
          .from('public_found_discs')
          .select('*')
          .or([
            `brand.ilike.${likeTerm}`,
            `mold.ilike.${likeTerm}`,
            `color.ilike.${likeTerm}`,
            `location_found.ilike.${likeTerm}`,
            `description.ilike.${likeTerm}`,
            `stamp_text.ilike.${likeTerm}`,
            `phone_number.ilike.${likeTerm}`,
            `name_on_disc.ilike.${likeTerm}`,
            `plastic_type.ilike.${likeTerm}`,
            ...nonTextOrClauses(term),
            `owner_email.ilike.${likeTerm}`
          ].join(','))
          .order('created_at', { ascending: false });

        if (orError) {
          let mainQuery = supabase
            .from('found_discs')
            .select('*')
            .eq('status', 'active')
            .order('created_at', { ascending: false });

          mainQuery = mainQuery.or([
            `brand.ilike.${likeTerm}`,
            `mold.ilike.${likeTerm}`,
            `color.ilike.${likeTerm}`,
            `location_found.ilike.${likeTerm}`,
            `description.ilike.${likeTerm}`,
            `stamp_text.ilike.${likeTerm}`,
            `phone_number.ilike.${likeTerm}`,
            `name_on_disc.ilike.${likeTerm}`,
            `plastic_type.ilike.${likeTerm}`,
            ...nonTextOrClauses(term),
            `owner_email.ilike.${likeTerm}`
          ].join(','));

          const res = await mainQuery;
          orData = res.data as any[] | null;
          orError = res.error;
        }

        if (!orError && orData) {
          const seen = new Set<string>();
          const combined = [
            ...(Array.isArray(rackIdResults) ? rackIdResults : []),
            ...excludeForSaleUsed(orData, includeForSaleUsed)
          ].filter(d => (seen.has(d.id) ? false : (seen.add(d.id), true)));

          const sorted = this.sortFoundDiscs(combined as any, 'newest');
          return { data: sorted, error: null, count: sorted.length, hasMore: false, nextOffset: 0 };
        }
      } catch (e) {
        console.warn('Chunked OR search failed; falling back to full fetch');
      }

      // If no exact rack_id match or it's a text search, fall back to full search
      console.log(`Performing full chunked search for term: "${term}"`);

      // Get all discs first, then filter in JavaScript for reliable search
      const { data: allDiscs, error: fetchError } = await this.getAllFoundDiscsChunked(includeForSaleUsed);

      if (fetchError) {
        throw fetchError;
      }

      if (!allDiscs || allDiscs.length === 0) {
        return { data: [], error: null, count: 0, hasMore: false, nextOffset: 0 };
      }

      // Filter discs that match the search term
      let filteredDiscs = allDiscs.filter(disc => {
        const textFields = [
          disc.brand,
          disc.mold,
          disc.color,
          disc.location_found,
          disc.description,
          disc.stamp_text,
          disc.phone_number,
          disc.name_on_disc,
          disc.owner_email,
          disc.owner_pdga_number ? String(disc.owner_pdga_number) : undefined,
          disc.plastic_type,
          disc.disc_type
        ];

        const matchesText = textFields.some(field =>
          field && field.toLowerCase().includes(lowerTerm)
        );

        const matchesRackId = isNumericSearch && disc.rack_id === rackIdNum;

        // UUID match on id
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        const matchesId = uuidRegex.test(term) && disc.id === term;

        // Numeric substring match on rack_id and phone ignoring formatting
        const termDigits = numericSearchDigits(term);
        const rackDigits = (disc.rack_id !== undefined && disc.rack_id !== null) ? String(disc.rack_id) : '';
        const matchesRackDigits = termDigits ? rackDigits.includes(termDigits) : false;
        const phoneDigits = (disc.phone_number || '').replace(/[^0-9]/g, '');
        const matchesPhoneDigits = termDigits ? phoneDigits.includes(termDigits) : false;

        return matchesText || matchesRackId || matchesId || matchesRackDigits || matchesPhoneDigits;
      });

      // Ensure rack_id/phone matches are included (merge/dedupe)
      const rackList2: FoundDisc[] = Array.isArray(rackIdResults) ? (rackIdResults as FoundDisc[]) : [];
      if (rackList2.length > 0) {
        const byId = new Map(filteredDiscs.map(d => [d.id, d]));
        for (const d of rackList2) {
          byId.set(d.id, d);
        }
        filteredDiscs = Array.from(byId.values());
      }

      console.log(`Completed single-term search: ${filteredDiscs.length} matching discs out of ${allDiscs.length} total`);
      return {
        data: filteredDiscs,
        error: null,
        count: filteredDiscs.length,
        hasMore: false,
        nextOffset: 0
      };
    } catch (error) {
      console.error('Error in chunked single-term search:', error);
      return { data: null, error, count: 0, hasMore: false, nextOffset: 0 };
    }
  },

  // Multi-term search with pagination (still needs client-side filtering)
  async searchFoundDiscsMultiTermPaginated(searchTerms: string[], limit: number, offset: number, sortBy: 'newest' | 'oldest' | 'rack_id_asc' | 'rack_id_desc' = 'newest', minRackId?: number, maxRackId?: number, includeForSaleUsed: boolean = false) {
    try {
      // For multi-term searches, we need to get a larger dataset and filter client-side
      // Get more records than requested to account for filtering
      const fetchLimit = Math.max(limit * 3, 1000); // Get 3x more records to account for filtering
      const { data: allDiscs, error: fetchError } = await this.getFoundDiscsPage(fetchLimit, 0, sortBy, minRackId, maxRackId, includeForSaleUsed);

      if (fetchError) {
        throw fetchError;
      }

      if (!allDiscs || allDiscs.length === 0) {
        return { data: [], error: null, count: 0, hasMore: false, nextOffset: 0 };
      }

      // Filter discs that match all search terms
      let filteredDiscs = allDiscs.filter(disc => {
        return searchTerms.every(term => {
          const lowerTerm = term.toLowerCase();

          const textFields = [
            disc.brand,
            disc.mold,
            disc.color,
            disc.location_found,
            disc.description,
            disc.stamp_text,
            disc.phone_number,
            disc.name_on_disc,
            disc.owner_email,
            disc.owner_pdga_number ? String(disc.owner_pdga_number) : undefined,
            disc.plastic_type,
            disc.disc_type
          ];

          const matchesText = textFields.some(field =>
            field && field.toLowerCase().includes(lowerTerm)
          );

          // Handle rack ID searches - strip # prefix if present
          const cleanTerm = term.startsWith('#') ? term.substring(1) : term;
          const rackIdNum = parseInt(cleanTerm);
          const matchesRackId = !isNaN(rackIdNum) && disc.rack_id === rackIdNum;

          // Numeric substring match on rack_id and phone ignoring formatting
          const termDigits = numericSearchDigits(term);
          const rackDigits = (disc.rack_id !== undefined && disc.rack_id !== null) ? String(disc.rack_id) : '';
          const matchesRackDigits = termDigits ? rackDigits.includes(termDigits) : false;
          const phoneDigits = (disc.phone_number || '').replace(/[^0-9]/g, '');
          const matchesPhoneDigits = termDigits ? phoneDigits.includes(termDigits) : false;

          // Direct UUID id match
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          const matchesId = uuidRegex.test(term) && disc.id === term;

          return matchesText || matchesRackId || matchesId || matchesRackDigits || matchesPhoneDigits;
        });
      });

      // Apply rack ID filters
      if (minRackId !== undefined) {
        filteredDiscs = filteredDiscs.filter(disc => disc.rack_id && disc.rack_id >= minRackId);
      }
      if (maxRackId !== undefined) {
        filteredDiscs = filteredDiscs.filter(disc => disc.rack_id && disc.rack_id <= maxRackId);
      }

      // Apply sorting
      filteredDiscs = this.sortFoundDiscs(filteredDiscs, sortBy);

      // Apply pagination to filtered results
      const paginatedResults = filteredDiscs.slice(offset, offset + limit);

      return {
        data: paginatedResults,
        error: null,
        count: filteredDiscs.length,
        hasMore: offset + limit < filteredDiscs.length,
        nextOffset: offset + limit
      };
    } catch (error) {
      console.error('Error in multi-term paginated search:', error);
      return { data: null, error, count: 0, hasMore: false, nextOffset: 0 };
    }
  },

  // Multi-term search using chunked fetching
  async searchFoundDiscsMultiTermChunked(searchTerms: string[], includeForSaleUsed: boolean = false) {
    try {
      console.log(`Searching for multi-term "${searchTerms.join(' ')}" using chunked approach...`);

      // Get all discs first, then filter in JavaScript for complex multi-term search
      const { data: allDiscs, error: fetchError } = await this.getAllFoundDiscsChunked(includeForSaleUsed);

      if (fetchError) {
        throw fetchError;
      }

      if (!allDiscs || allDiscs.length === 0) {
        return { data: [], error: null };
      }

      // Filter discs that match all search terms
      const filteredDiscs = allDiscs.filter(disc => {
        return searchTerms.every(term => {
          const lowerTerm = term.toLowerCase();

          const textFields = [
            disc.brand,
            disc.mold,
            disc.color,
            disc.location_found,
            disc.description,
            disc.stamp_text,
            disc.phone_number,
            disc.name_on_disc,
            disc.owner_email,
            disc.owner_pdga_number ? String(disc.owner_pdga_number) : undefined,
            disc.plastic_type,
            disc.disc_type
          ];

          const matchesText = textFields.some(field =>
            field && field.toLowerCase().includes(lowerTerm)
          );

          // Handle rack ID searches - strip # prefix if present
          const cleanTerm = term.startsWith('#') ? term.substring(1) : term;
          const rackIdNum = parseInt(cleanTerm);
          const matchesRackId = !isNaN(rackIdNum) && disc.rack_id === rackIdNum;

          // Numeric substring match on rack_id and phone ignoring formatting (multi-term chunked)
          const termDigits = numericSearchDigits(term);
          const rackDigits = (disc.rack_id !== undefined && disc.rack_id !== null) ? String(disc.rack_id) : '';
          const matchesRackDigits = termDigits ? rackDigits.includes(termDigits) : false;
          const phoneDigits = (disc.phone_number || '').replace(/[^0-9]/g, '');
          const matchesPhoneDigits = termDigits ? phoneDigits.includes(termDigits) : false;

          return matchesText || matchesRackId || matchesRackDigits || matchesPhoneDigits;
        });
      });

      console.log(`Completed multi-term search: ${filteredDiscs.length} matching discs out of ${allDiscs.length} total`);
      return {
        data: filteredDiscs,
        error: null,
        count: filteredDiscs.length,
        hasMore: false,
        nextOffset: 0
      };
    } catch (error) {
      console.error('Error in multi-term chunked search:', error);
      return { data: null, error, count: 0, hasMore: false, nextOffset: 0 };
    }
  },

  // Test connection to Supabase
  async testConnection() {
    try {
      const { error } = await supabase
        .from('found_discs')
        .select('count')
        .limit(1)

      return { connected: !error, error }
    } catch (error) {
      return { connected: false, error }
    }
  },

  // Update return status of a found disc (admin only)
  async updateReturnStatus(discId: string, returnStatus: ReturnStatus, notes?: string) {
    try {
      const { error } = await supabase.rpc('update_disc_return_status', {
        disc_id: discId,
        new_status: returnStatus,
        notes: notes || null
      });

      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating return status:', error);
      return { success: false, error };
    }
  },



  // Get all found discs for admin (includes all return statuses)
  async getAdminFoundDiscs() {
    try {
      const { data, error } = await supabase
        .from('admin_found_discs')
        .select('*');

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching admin found discs:', error);
      return { data: null, error };
    }
  },

  // Admin: full found_discs row for editing (the public view lacks source_id and some statuses)
  async getFoundDiscForEdit(discId: string) {
    try {
      const { data, error } = await supabase
        .from('found_discs')
        .select('*')
        .eq('id', discId)
        .single();

      if (error) throw error;
      return { data: data as FoundDisc, error: null };
    } catch (error) {
      console.error('Error fetching found disc for edit:', error);
      return { data: null, error };
    }
  },

  // Admin: update a found disc's details (RLS limits this to admins and the disc's finder)
  async updateFoundDisc(discId: string, updates: Partial<Record<keyof FoundDisc, unknown>>) {
    try {
      const { data, error } = await supabase
        .from('found_discs')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', discId)
        .select('id');

      if (error) throw error;
      // RLS-blocked updates don't error, they just match no rows
      if (!data || data.length === 0) throw new Error('Not allowed to edit this disc');
      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating found disc:', error);
      return { success: false, error };
    }
  },

  // Bulk Turn-In Functions

  // Create a new bulk turn-in record (rakerdiver only)
  async createBulkTurnin(turninData: {
    location_collected: string;
    collection_date: string;
    collection_time?: string;
    disc_count: number;
    turnin_location: string;
    turnin_date: string;
    turnin_time?: string;
    notes?: string;
  }) {
    try {
      const { data, error } = await supabase.rpc('create_bulk_turnin', {
        p_location_collected: turninData.location_collected,
        p_collection_date: turninData.collection_date,
        p_disc_count: turninData.disc_count,
        p_turnin_location: turninData.turnin_location,
        p_turnin_date: turninData.turnin_date,
        p_collection_time: turninData.collection_time || null,
        p_turnin_time: turninData.turnin_time || null,
        p_notes: turninData.notes || null
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating bulk turn-in:', error);
      return { data: null, error };
    }
  },

  // Get bulk turn-ins for current rakerdiver
  async getRakerdiverTurnins() {
    try {
      const { data, error } = await supabase.rpc('get_rakerdiver_turnins');

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching rakerdiver turn-ins:', error);
      return { data: null, error };
    }
  },

  // Verify a bulk turn-in (admin only)
  async verifyBulkTurnin(turninId: string, verificationNotes?: string) {
    try {
      const { error } = await supabase.rpc('verify_bulk_turnin', {
        p_turnin_id: turninId,
        p_verification_notes: verificationNotes || null
      });

      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      console.error('Error verifying bulk turn-in:', error);
      return { success: false, error };
    }
  },

  // Create a payment record (admin only)
  async createBulkTurninPayment(paymentData: {
    bulk_turnin_id: string;
    amount: number;
    payment_method?: string;
    payment_date?: string;
    payment_notes?: string;
  }) {
    try {
      const { data, error } = await supabase.rpc('create_bulk_turnin_payment', {
        p_bulk_turnin_id: paymentData.bulk_turnin_id,
        p_amount: paymentData.amount,
        p_payment_method: paymentData.payment_method || null,
        p_payment_date: paymentData.payment_date || null,
        p_payment_notes: paymentData.payment_notes || null
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating payment:', error);
      return { data: null, error };
    }
  },

  // Confirm payment receipt (rakerdiver only)
  async confirmPaymentReceipt(paymentId: string) {
    try {
      const { error } = await supabase.rpc('confirm_payment_receipt', {
        p_payment_id: paymentId
      });

      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      console.error('Error confirming payment:', error);
      return { success: false, error };
    }
  },

  // Get all bulk turn-ins for admin
  async getAdminBulkTurnins() {
    try {
      const { data, error } = await supabase
        .from('admin_bulk_turnins')
        .select('*');

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching admin bulk turn-ins:', error);
      return { data: null, error };
    }
  },

  // Get payments for a specific bulk turn-in
  async getBulkTurninPayments(turninId: string) {
    try {
      const { data, error } = await supabase
        .from('bulk_turnin_payments')
        .select('*')
        .eq('bulk_turnin_id', turninId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching payments:', error);
      return { data: null, error };
    }
  }
}

// Image upload service
export const imageService = {
  // Upload multiple images to Supabase storage
  async uploadImages(files: File[], userId: string): Promise<{ urls: string[], error: any }> {
    try {
      console.log(`Starting upload of ${files.length} files for user ${userId}`);

      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log('User authenticated:', user.id);

      const uploadPromises = files.map(async (file, index) => {
        // Generate unique filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${userId}/${Date.now()}-${index}.${fileExt}`;

        console.log(`Uploading file ${index + 1}/${files.length}: ${fileName}`);

        const { data, error } = await supabase.storage
          .from('disc-images')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) {
          console.error(`Upload error for ${fileName}:`, error);
          throw error;
        }

        console.log(`Upload successful for ${fileName}:`, data);

        // Get public URL
        const { data: urlData } = supabase.storage
          .from('disc-images')
          .getPublicUrl(fileName);

        console.log(`Public URL for ${fileName}:`, urlData.publicUrl);
        return urlData.publicUrl;
      });

      const urls = await Promise.all(uploadPromises);
      console.log('All uploads completed:', urls);
      return { urls, error: null };
    } catch (error) {
      console.error('Error uploading images:', error);
      return { urls: [], error };
    }
  },

  // Delete images from Supabase storage
  async deleteImages(imageUrls: string[]): Promise<{ success: boolean, error: any }> {
    try {
      // Extract file paths from URLs
      const filePaths = imageUrls.map(url => {
        const urlParts = url.split('/');
        const bucketIndex = urlParts.findIndex(part => part === 'disc-images');
        if (bucketIndex !== -1 && bucketIndex < urlParts.length - 1) {
          return urlParts.slice(bucketIndex + 1).join('/');
        }
        return null;
      }).filter(Boolean) as string[];

      if (filePaths.length === 0) {
        return { success: true, error: null };
      }

      const { error } = await supabase.storage
        .from('disc-images')
        .remove(filePaths);

      return { success: !error, error };
    } catch (error) {
      console.error('Error deleting images:', error);
      return { success: false, error };
    }
  },

  // Get optimized image URL (for future use with Supabase image transformations)
  getOptimizedImageUrl(originalUrl: string, width?: number, height?: number): string {
    // For now, return original URL
    // In the future, you can add Supabase image transformation parameters
    return originalUrl;
  },

  // Validate image file
  validateImageFile(file: File, maxSizeMB: number = 10): { valid: boolean, error?: string } {
    // Check file type
    if (!file.type.startsWith('image/')) {
      return { valid: false, error: 'File must be an image' };
    }

    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return { valid: false, error: `Image size must be less than ${maxSizeMB}MB` };
    }

    // Check supported formats
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!supportedTypes.includes(file.type)) {
      return { valid: false, error: 'Supported formats: JPEG, PNG, WebP' };
    }

    return { valid: true };
  }
}

// Export the service instance
export const supabaseService = {
  getSources: async (): Promise<Source[]> => {
    const { data, error } = await supabase
      .from('sources')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching sources:', error);
      throw error;
    }

    return data || [];
  },

  getActiveSources: async (): Promise<Source[]> => {
    const { data, error } = await supabase
      .from('sources')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching active sources:', error);
      throw error;
    }

    return data || [];
  },

  createSource: async (source: Omit<Source, 'id' | 'created_at' | 'updated_at'>): Promise<Source> => {
    const { data, error } = await supabase
      .from('sources')
      .insert([source])
      .select()
      .single();

    if (error) {
      console.error('Error creating source:', error);
      throw error;
    }

    return data;
  },

  updateSource: async (id: string, updates: Partial<Omit<Source, 'id' | 'created_at' | 'updated_at'>>): Promise<Source> => {
    const { data, error } = await supabase
      .from('sources')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating source:', error);
      throw error;
    }

    return data;
  },

  deleteSource: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('sources')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting source:', error);
      throw error;
    }
  },

  // Contact Attempts
  getContactAttempts: async (discId: string) => {
    try {
      const { data, error } = await supabase
        .from('contact_attempts')
        .select(`
          *,
          attempted_by_profile:profiles!attempted_by_profile_id(
            id,
            full_name,
            email
          )
        `)
        .eq('found_disc_id', discId)
        .order('attempted_at', { ascending: false });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching contact attempts:', error);
      return { success: false, error };
    }
  },

  addContactAttempt: async (contactAttempt: Omit<ContactAttempt, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('contact_attempts')
        .insert([contactAttempt])
        .select();

      if (error) throw error;
      return { success: true, data: data?.[0] };
    } catch (error) {
      console.error('Error adding contact attempt:', error);
      return { success: false, error };
    }
  },

  updateContactAttempt: async (id: string, updates: Partial<ContactAttempt>) => {
    try {
      const { data, error } = await supabase
        .from('contact_attempts')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select();

      if (error) throw error;
      return { success: true, data: data?.[0] };
    } catch (error) {
      console.error('Error updating contact attempt:', error);
      return { success: false, error };
    }
  },

  // Default Source Management
  getUserDefaultSource: async (userId: string): Promise<Source | null> => {
    try {
      // First get the user's default_source_id
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('default_source_id')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      // If no default source is set, return null
      if (!profileData?.default_source_id) {
        return null;
      }

      // Get the source details
      const { data: sourceData, error: sourceError } = await supabase
        .from('sources')
        .select('*')
        .eq('id', profileData.default_source_id)
        .eq('is_active', true)
        .single();

      if (sourceError) {
        // If source doesn't exist or is inactive, return null
        return null;
      }

      return sourceData as Source;
    } catch (error) {
      console.error('Error fetching user default source:', error);
      return null;
    }
  },

  updateUserDefaultSource: async (userId: string, sourceId: string | null): Promise<{ success: boolean; error?: any }> => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          default_source_id: sourceId,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error updating user default source:', error);
      return { success: false, error };
    }
  }
};
